import { useCallback, useMemo, useState } from 'react';
import Avatar from '../common/AvatarContainer';
import './ChatMessage.scss';
import { AskRequestStatus, User } from '@hone-automation/common';
import { useQueueContext } from '../QueueContext';
import DOMPurify from 'dompurify';
import { convertFromShortcodes } from '../utils/emojiParser';
import { format } from 'date-fns';
import ResponsiveAttachments from '../common/ResponsiveAttachments/ResponsiveAttachments';
import { Attachment } from '../Chat/Chat';
import { IconMentionPurple, IconMore } from 'components/Icons';
import MessageComposer from '../CMComposer/CMComposer';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import Modal from 'react-modal';
import { usePatchQueueCommentMutation, useDeleteQueueCommentMutation } from 'hooks/queue';
import { useParams } from 'react-router-dom';
import { useGetAllUsersByLocation } from 'hooks';
import { mentionUtils } from '../CMComposer/mentionUtils';
import { useAuth } from 'context/useAuthContext';
import { ProfileEntryParams } from 'presentation/components/NewTable/types';
import { mergeActiveAndCommentUsers } from '../utils/userUtils';

interface ChatMessageProps {
  mobile?: boolean;
  user?: User;
  editable: boolean;
  date?: Date;
  showDate: boolean;
  content: string;
  glCode?: boolean;
  attachments?: Attachment[];
  createdAt: Date;
  id?: string;
  onEdit?: (id: string, content: string) => void;
  onDelete?: (id: string) => void;
  canDelete: boolean;
  canUpdate: boolean;
  edited: boolean;
  isFirstMessage?: boolean;
  isHoneInternalUser?: boolean;
  isInternal?: boolean;
  allUsersFromComments?: User[];
}

/**
 * Renders a chat message component with user details, content, and optional date display.
 *
 * @param {ChatMessageProps} props - The properties for rendering the chat message.
 * @param {User} [props.user] - Optional user information for the message.
 * @param {boolean} props.showDate - Flag to determine whether to display the message date.
 * @param {Date} [props.date] - Optional date of the message.
 * @param {string} props.content - The content of the chat message.
 * @param {Attachment[]} [props.attachments] - Optional attachments associated with the message.
 * @param {string} [props.id] - Optional message ID for editing purposes.
 * @param {Function} [props.onEdit] - Optional callback function for editing messages.
 * @param {boolean} props.canDelete - Flag to determine whether to display the delete button.
 * @param {boolean} props.canUpdate - Flag to determine whether to display the edit message button.
 * @param {boolean} props.edited - Flag to determine whether the comment has been edited.
 * @param {boolean} [props.isFirstMessage] - Optional flag to determine whether the message is the first message in the chat.
 * @param {boolean} [props.isHoneInternalUser] - Optional flag to determine whether the message is from a Hone internal user.
 * @returns {JSX.Element} A rendered chat message component.
 */
function ChatMessage({
  user,
  showDate,
  date,
  content,
  attachments,
  id,
  onEdit,
  onDelete,
  editable,
  canDelete,
  canUpdate,
  edited,
  isFirstMessage,
  isHoneInternalUser,
  isInternal,
  mobile = false,
  allUsersFromComments = [],
}: ChatMessageProps) {
  const {
    state: { meId, selectedItem, requests },
    dispatch,
  } = useQueueContext();
  const { userId } = useAuth();
  const { locationId: routeLocationId } = useParams<ProfileEntryParams>();
  const { data: usersFetched } = useGetAllUsersByLocation(routeLocationId);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { locationId } = useParams<{ locationId: string }>();

  // Get the current request status
  const status = selectedItem?.status as AskRequestStatus;

  // Initialize the mutations
  const patchCommentMutation = usePatchQueueCommentMutation(status, locationId!, data => {
    // Success callback will be called after the comment is updated
    if (onEdit) {
      onEdit(id!, data.comment);
    }

    // Update the comment in UI immediately
    const updatedRequests = requests.map(req => {
      if (req.id === selectedItem?.id) {
        // Update the specific comment in the request's comments array
        return {
          ...req,
          comments:
            req.comments?.map(comment =>
              comment.id === id
                ? {
                    ...comment,
                    comment: data.comment,
                    updatedAt: new Date(), // Ensure updatedAt is a Date object
                  }
                : comment
            ) || [],
        };
      }
      return req;
    });

    // Update the Queue context with the updated requests list
    dispatch({
      type: 'SET_REQUESTS',
      payload: updatedRequests,
    });

    // Also update the selected item to reflect the updated comment
    if (selectedItem) {
      const updatedSelectedItem = {
        ...selectedItem,
        comments:
          selectedItem.comments?.map(comment => (comment.id === id ? { ...data, updatedAt: new Date() } : comment)) ||
          [],
      };

      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: updatedSelectedItem,
      });
    }
  });

  const deleteCommentMutation = useDeleteQueueCommentMutation(status, locationId!, () => {
    // Success callback will be called after the comment is deleted
    if (onDelete) {
      onDelete(id!);
    }

    // Remove the comment from UI immediately
    const updatedRequests = requests.map(req => {
      if (req.id === selectedItem?.id) {
        // Filter out the deleted comment from the request's comments
        return {
          ...req,
          comments: req.comments?.filter(comment => comment.id !== id) || [],
        };
      }
      return req;
    });

    // Update the Queue context with the updated requests list
    dispatch({
      type: 'SET_REQUESTS',
      payload: updatedRequests,
    });

    // Also update the selected item to reflect the deleted comment
    if (selectedItem) {
      const updatedSelectedItem = {
        ...selectedItem,
        comments: selectedItem.comments?.filter(comment => comment.id !== id) || [],
      };

      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: updatedSelectedItem,
      });
    }
  });

  const handleEdit = () => {
    setShowDropdown(false);
    setIsEditing(true);
  };

  const handleDelete = () => {
    setShowDropdown(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      deleteCommentMutation.mutate(id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = (updatedContent: string) => {
    if (id) {
      patchCommentMutation.mutate({
        id,
        comment: updatedContent,
      });
      setIsEditing(false);
    }
  };

  const modalStyle = {
    content: {
      width: '600px',
      top: '50%',
      transform: 'translateY(-50%)',
      height: 'fit-content',
      margin: '0 auto',
    },
  };

  const userInfo = {
    userName: user?.name || '',
    userId: user?.userId || '',
    title: '',
    email: user?.name || '',
    colorId: '1',
  };

  const time = date && format(date, 'h:mm a');
  const chatDate = date && format(date, 'LLL do, yyyy');
  const formatUrlsInText = (text: string): string => {
    // This regex matches URLs that are not already part of an <a> tag
    const urlRegex = /(https?:\/\/[^\s<>"]+)/g;

    return text.replace(urlRegex, (match, url, offset) => {
      // Check if URL is already inside an href attribute or anchor tag
      const beforeUrl = text.substring(Math.max(0, offset - 20), offset);
      const afterUrl = text.substring(offset + match.length, Math.min(text.length, offset + match.length + 20));

      // Skip if preceded by href=" or href=' or href=
      if (/href\s*=\s*["']?$/.test(beforeUrl)) {
        return match;
      }

      // Skip if it appears to be inside an anchor tag
      if (/<a[^>]*$/.test(beforeUrl) && /^[^<]*<\/a>/.test(afterUrl)) {
        return match;
      }

      // Otherwise, wrap it in an <a> tag
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
  };

  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ADD_ATTR: ['target', 'rel'],
    KEEP_CONTENT: true,
  };

  // Add hook to ensure all anchor tags open in new tab
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  const sanitizedContent = formatUrlsInText(convertFromShortcodes(DOMPurify.sanitize(content, purifyConfig)));

  // Remove the hook after sanitization to avoid side effects
  DOMPurify.removeHook('afterSanitizeAttributes');

  // Merge active users with users from comments to include deactivated users
  const allUsers = useMemo(
    () => mergeActiveAndCommentUsers(usersFetched, allUsersFromComments),
    [usersFetched, allUsersFromComments]
  );

  const formatMentions = useCallback(
    (content: string, skipAutoFormat: boolean = false) => {
      if (skipAutoFormat) {
        return content;
      }
      return mentionUtils.formatMentionsToHtml(content, allUsers);
    },
    [allUsers]
  );

  const parsedContent = { __html: formatMentions(sanitizedContent) };

  // We calculate KAI from either the username or the userId. UserId = 4emD3RxpsUcnDgndL2vMbHryHJU2 is the hone admin user user
  const isKit =
    userInfo.userName.toLowerCase() === 'kit' ||
    userInfo.userId === '4emD3RxpsUcnDgndL2vMbHryHJU2' ||
    userInfo.userName.toLowerCase() === 'hone admin';

  const hasMentions = content.match(/::[A-Za-z0-9]+::/g) && content.includes(`::${userId}::`);

  return (
    <>
      {showDate && date && <div className="chat-block-date">{chatDate}</div>}
      <div className={`message ${isFirstMessage ? 'first-child' : ''}`}>
        <div className="message__avatar-container">
          <div className="message__avatar-container-mentions">{hasMentions && <IconMentionPurple />}</div>
          <Avatar
            // NOTE: HD-1949 when AssignmentAPI is available use this
            // me={user?.userId === meId}
            me={false}
            userInfo={userInfo}
            isHoneInternalUser={isHoneInternalUser}
          />
        </div>
        {isEditing ? (
          <div className="message__edit-container">
            <MessageComposer
              request={useQueueContext().state.selectedItem!}
              initialContent={content}
              isEdit={true}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
          </div>
        ) : (
          <div className="message__content">
            <div className="message__header">
              <div className="message__sender-info">
                <div className="message__sender-info-user">
                  <span className="message__sender">{isKit ? 'KAI' : userInfo.userName}</span>
                  {isHoneInternalUser && <div className="message__sender-ks">{isKit ? 'System' : 'Accountant'}</div>}
                  {isInternal && <div className="message__sender-internal">Internal</div>}
                </div>
                <span className="message__time">{time}</span>
              </div>
              {editable && (
                <button className="message__more-button" onClick={() => setShowDropdown(!showDropdown)}>
                  <IconMore />
                </button>
              )}
              {showDropdown && (
                <div className="message__dropdown">
                  {canUpdate && (
                    <button className="message__dropdown-item" onClick={handleEdit}>
                      Edit Message
                    </button>
                  )}
                  {canDelete && (
                    <button className="message__dropdown-item message__dropdown-item--delete" onClick={handleDelete}>
                      Delete Message...
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="message__wrapper">
              <p className="message__text" dangerouslySetInnerHTML={parsedContent}></p>
              {edited && <span>(edited)</span>}
            </div>
            {/* TODO */}
            {attachments && <ResponsiveAttachments attachments={attachments} isMobile={mobile} />}
          </div>
        )}
      </div>

      {showDeleteModal && (
        <Modal
          ariaHideApp={false}
          style={modalStyle}
          isOpen={showDeleteModal}
          onRequestClose={() => setShowDeleteModal(false)}
        >
          <ConfirmModal
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            title={'Delete Message'}
          >
            <div>
              <p>Are you sure you want to delete this message? This step cannot be undone.</p>
              <div className="message-preview">
                <p dangerouslySetInnerHTML={parsedContent}></p>
              </div>
            </div>
          </ConfirmModal>
        </Modal>
      )}
    </>
  );
}

export default ChatMessage;
